import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box, CircularProgress } from "@mui/material";
import axios from "axios";
import { useTranslation } from "react-i18next";
import ApplicationTable from "./ApplicationTable";
import ApplicationTable2 from "./ApplicationsTable2";
import useUnifiedInfiniteScroll from "../../hooks/useUnifiedInfiniteScroll";
import InfiniteScrollIndicator from "../../components/InfiniteScrollIndicator";

const apiUrl = process.env.REACT_APP_API_URL;

const CandidatesInfiniteScroll = ({
  viewType,
  setViewType,
  onNavigateToBlacklist = null,
  onNavigateToDynamicForms = null,
  infiniteScrollPageSize = 2,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [currentSearchParams, setCurrentSearchParams] = useState({});
  const initializationRef = useRef(false);

  // Helper function to parse JWT token
  const parseJwt = (token) => {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  };

  // Helper function to get user ID from access token
  const getUserIdFromAccessToken = () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      console.error("No access token found in local storage");
      return null;
    }

    try {
      const decodedToken = parseJwt(accessToken);
      return decodedToken.user_id;
    } catch (error) {
      console.error("Failed to decode access token", error);
      return null;
    }
  };

  // Create fetch function specifically for candidates
  const fetchCandidatesData = useCallback(async (page, pageSize, searchParams = {}) => {
    const recruiterId = getUserIdFromAccessToken();
    if (!recruiterId) {
      console.error("No recruiter ID available");
      return { results: [], count: 0, hasNext: false };
    }

    const endpoint = `${apiUrl}candidates/api/unified-candidates/${recruiterId}/`;

    // Use current search parameters if no searchParams are provided
    const finalSearchParams = Object.keys(searchParams).length > 0 ? searchParams : currentSearchParams;

    const params = {
      page,
      page_size: pageSize,
      ...finalSearchParams,
    };

    console.log("🔍 CANDIDATES INFINITE SCROLL - Making API call to:", endpoint, "with params:", params);

    try {
      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        params,
      });

      const responseData = response.data;
      let results = [];
      let count = 0;
      let hasNext = false;

      if (responseData.results) {
        results = responseData.results || [];
        count = responseData.count || results.length;
        hasNext = responseData.next !== null;
      } else if (Array.isArray(responseData)) {
        results = responseData;
        count = results.length;
        hasNext = false;
      }

      // Map source to dataType for unified candidates
      const normalizedResults = results.map(candidate => {
        if (candidate.source && !candidate.dataType) {
          return {
            ...candidate,
            dataType: candidate.source
          };
        }
        return candidate;
      });

      console.log("🔍 CANDIDATES INFINITE SCROLL - Returning results:", {
        resultsCount: normalizedResults.length,
        totalCount: count,
        hasNext: hasNext
      });

      return {
        results: normalizedResults,
        count,
        hasNext,
      };
    } catch (error) {
      console.error("Error fetching candidates data:", error);
      return { results: [], count: 0, hasNext: false };
    }
  }, [currentSearchParams]);

  // Initialize unified infinite scroll hook specifically for candidates
  const {
    data: infiniteScrollData,
    loading: infiniteScrollLoading,
    hasMore,
    loadMore,
    reset: resetInfiniteScroll,
    loadInitial: loadInitialInfiniteScroll,
    setDataDirectly: setInfiniteScrollDataDirectly,
    error: infiniteScrollError,
    totalCount: infiniteScrollTotalCount,
    lastElementRef,
  } = useUnifiedInfiniteScroll({
    fetchData: fetchCandidatesData,
    initialData: [],
    initialTotalCount: 0,
    pageSize: infiniteScrollPageSize,
    dependencies: [], // No dependencies to prevent resets
    enabled: true,
  });

  // Initialize data on component mount
  useEffect(() => {
    // Prevent multiple initializations
    if (initializationRef.current) {
      console.log("🚀 CANDIDATES INFINITE SCROLL - Skipping re-initialization");
      return;
    }

    const initializeData = async () => {
      initializationRef.current = true;
      setLoading(true);
      try {
        console.log("🚀 CANDIDATES INFINITE SCROLL - Initializing data...");
        await loadInitialInfiniteScroll();
      } catch (error) {
        console.error("Error initializing candidates data:", error);
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, []); // Empty dependency array to prevent resets

  // Handle search results for candidates
  const handleSearchResults = (results, totalCount = null, searchParams = {}) => {
    console.log("🔍 CANDIDATES SEARCH - handleSearchResults called with:", {
      results: results,
      totalCount: totalCount,
      searchParams: searchParams
    });

    // Handle null results (clear button clicked)
    if (results === null) {
      console.log("🔍 CANDIDATES SEARCH - Clear button clicked, resetting...");
      resetInfiniteScroll();
      setTimeout(() => {
        loadInitialInfiniteScroll();
      }, 100);
      return;
    }

    if (Array.isArray(results)) {
      console.log("🔍 CANDIDATES SEARCH - Processing array results");

      // Map source to dataType for unified candidates
      const normalizedResults = results.map(candidate => {
        if (candidate.source && !candidate.dataType) {
          return {
            ...candidate,
            dataType: candidate.source
          };
        }
        return candidate;
      });

      const newTotalCount = totalCount !== null ? totalCount : normalizedResults.length;

      // Preserve scroll position when applying search/filters (true by default)
      setInfiniteScrollDataDirectly(normalizedResults, newTotalCount, true);

      // Store search parameters for future infinite scroll requests
      setCurrentSearchParams(searchParams);
    } else {
      console.error("Candidates search results are not an array:", results);
      resetInfiniteScroll();
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {viewType === "card" ? (
        <ApplicationTable
          candidates={infiniteScrollData}
          post_title=""
          viewType={viewType}
          setViewType={setViewType}
          dataType="mixed"
          initialTotalCount={infiniteScrollTotalCount}
          onTotalCountChange={() => {}} // Not needed for infinite scroll
          onDataChange={() => {}} // Not needed for infinite scroll
          onNavigateToBlacklist={onNavigateToBlacklist}
          onNavigateToDynamicForms={onNavigateToDynamicForms}
          enableInfiniteScroll={true}
          infiniteScrollPageSize={infiniteScrollPageSize}
          fetchMoreData={fetchCandidatesData}
          onSearchResults={handleSearchResults}
          infiniteScrollData={infiniteScrollData}
          infiniteScrollTotalCount={infiniteScrollTotalCount}
        />
      ) : (
        <ApplicationTable2
          data={infiniteScrollData}
          viewType={viewType}
          setViewType={setViewType}
          location=""
          dataType="mixed"
          initialTotalCount={infiniteScrollTotalCount}
          onTotalCountChange={() => {}} // Not needed for infinite scroll
          onDataChange={() => {}} // Not needed for infinite scroll
        />
      )}

      {/* Infinite Scroll Loading Indicator */}
      <InfiniteScrollIndicator
        loading={infiniteScrollLoading}
        hasMore={hasMore}
        dataLength={infiniteScrollData.length}
        error={infiniteScrollError}
        lastElementRef={lastElementRef}
        loadingText="applicationTable.loadingMore"
        noMoreText="applicationTable.noMoreCandidates"
        errorText="applicationTable.loadingError"
      />
    </Box>
  );
};

export default CandidatesInfiniteScroll;


